import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  CaretLeft,
  CaretRight,
  CircleNotch,
  ArrowsClockwise,
  Envelope,
  Warning,
} from '@phosphor-icons/react';
import hubspotIcon from '@/assets/providers/hubspot.png';
import salesforceIcon from '@/assets/providers/salesforce.webp';
import pipedriveIcon from '@/assets/providers/pipedrive.png';
import { Button } from '../../../components/ui/Button';
import { ContactListCombobox, NO_LIST, type ContactListSelection } from '../../../components/ui/ContactListCombobox';
import { useContactStore } from '../../../stores/contactStore';
import { integrationsApi } from '../../../lib/api';
import { useScrollLock } from '../../../lib/useScrollLock';
import { Tooltip } from '@/components/ui/Tooltip';
import { toast } from 'sonner';

// CRMs that support browse-and-import through the generic /crm endpoints.
export type CrmImportProvider = 'hubspot' | 'salesforce' | 'pipedrive';

// `fill` = full-bleed colored logo tile (object-cover); otherwise white-boxed wordmark.
const PROVIDER_META: Record<CrmImportProvider, { name: string; logo: string; fill?: boolean }> = {
  hubspot: { name: 'HubSpot', logo: hubspotIcon, fill: true },
  salesforce: { name: 'Salesforce', logo: salesforceIcon },
  pipedrive: { name: 'Pipedrive', logo: pipedriveIcon, fill: true },
};

// Normalized record shape returned by /integrations/crm/contacts.
interface CrmPerson {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  title?: string;
  phone_number?: string;
  organization_name?: string;
  organization_website?: string;
  organization_industry?: string;
  city?: string;
  state?: string;
  country?: string;
}

const PER_PAGE = 50;

interface CrmImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: CrmImportProvider | null;
}

export function CrmImportModal({ isOpen, onClose, provider }: CrmImportModalProps) {
  useScrollLock(isOpen);
  const { crmImport, contactLists, fetchContactLists, createContactList } = useContactStore();

  const [people, setPeople] = useState<CrmPerson[]>([]);
  // Cursor-based paging: `history` holds the cursor used to load each visited
  // page (index 0 = first page, cursor null). `pageIndex` points at the current
  // page; `nextCursor` is the token for the page after it (null = no more).
  const [history, setHistory] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [listSel, setListSel] = useState<ContactListSelection>(NO_LIST);
  const [tagsInput, setTagsInput] = useState('');
  const [importing, setImporting] = useState(false);

  const meta = provider ? PROVIDER_META[provider] : null;

  const fetchPeople = useCallback(async (prov: CrmImportProvider, cursor: string | null) => {
    setLoading(true);
    setLoadError(null);
    setSelected(new Set());
    try {
      const res = await integrationsApi.crmContacts({ provider: prov, cursor, per_page: PER_PAGE });
      setPeople(res.data.people || []);
      setNextCursor(res.data.next_cursor ?? null);
    } catch (error: any) {
      setPeople([]);
      setNextCursor(null);
      setLoadError(error.response?.data?.detail || `Failed to load records from ${PROVIDER_META[prov].name}.`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && provider) {
      setHistory([null]);
      setPageIndex(0);
      setTagsInput(`${provider}-import`);
      fetchPeople(provider, null);
      fetchContactLists();
    }
     
  }, [isOpen, provider]);

  const goNext = () => {
    if (!provider || nextCursor == null) return;
    const newHistory = history.slice(0, pageIndex + 1);
    newHistory.push(nextCursor);
    setHistory(newHistory);
    setPageIndex(pageIndex + 1);
    fetchPeople(provider, nextCursor);
  };

  const goPrev = () => {
    if (!provider || pageIndex === 0) return;
    const prev = pageIndex - 1;
    setPageIndex(prev);
    fetchPeople(provider, history[prev]);
  };

  const refresh = () => {
    if (provider) fetchPeople(provider, history[pageIndex]);
  };

  const withEmailCount = Array.from(selected).filter((idx) => people[idx]?.email).length;

  const toggleSelect = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const selectableIndices = people
    .map((p, idx) => (p.email ? idx : -1))
    .filter((idx) => idx >= 0);
  const allSelected = selectableIndices.length > 0 && selectableIndices.every((idx) => selected.has(idx));

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableIndices));
  };

  const handleImport = async () => {
    if (!provider || !meta) return;
    const chosen = people.filter((p, idx) => selected.has(idx) && p.email);
    if (chosen.length === 0) {
      toast.error('Select at least one record with an email address');
      return;
    }
    const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
    setImporting(true);
    try {
      // Resolve the destination list. A typed-in new name is created first
      // (backend takes a numeric list_id only), then reused for later imports.
      let listId: number | undefined;
      let listName: string | undefined;
      if (listSel.mode === 'existing') {
        listId = listSel.id;
        listName = listSel.name;
      } else if (listSel.mode === 'new') {
        const created = await createContactList({ name: listSel.name.trim(), contact_ids: [] });
        listId = created.id;
        listName = created.name;
        setListSel({ mode: 'existing', id: created.id, name: created.name });
      }
      const result = await crmImport(
        provider,
        chosen,
        tags.length > 0 ? tags : undefined,
        listId,
      );
      // When a list is chosen, both new and already-existing contacts are added
      // to it, so report the actual number added (added_to_list) rather than
      // saying duplicates were "skipped" (they weren't — they went to the list).
      if (listName) {
        const added = result.added_to_list ?? result.imported;
        toast.success(
          `Added ${added} contact${added === 1 ? '' : 's'} to "${listName}"` +
          (result.duplicates > 0
            ? ` (${result.imported} new, ${result.duplicates} already in your contacts)`
            : '')
        );
      } else {
        toast.success(
          `Imported ${result.imported} contact${result.imported === 1 ? '' : 's'}` +
          (result.duplicates > 0 ? ` (${result.duplicates} duplicate${result.duplicates === 1 ? '' : 's'} skipped)` : '')
        );
      }
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || `Failed to import contacts from ${meta.name}`);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setPeople([]); setSelected(new Set()); setHistory([null]); setPageIndex(0);
    setNextCursor(null); setLoadError(null); setListSel(NO_LIST); setTagsInput('');
    onClose();
  };

  // Escape dismissal. Routed through handleClose (never onClose directly) so the
  // browse/selection state is reset the same way a backdrop click resets it.
  // Must stay above the early return — hooks can't run conditionally.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
     
  }, [isOpen, onClose]);

  if (!isOpen || !provider || !meta) return null;
  if (typeof document === 'undefined') return null;

  // Pipedrive Persons have no native job-title or address fields, so title and
  // location never come back on import. Hide those columns for Pipedrive rather
  // than showing perpetually empty cells. HubSpot and Salesforce map them natively.
  const showTitleLocation = provider !== 'pipedrive';

  return createPortal((
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="crm-import-title"
          onClick={(e) => e.stopPropagation()}
          style={{ height: '62dvh' }}
          className="w-full max-w-5xl flex flex-col rounded-xl overflow-hidden bg-card border border-border/60 shadow-xl dark:bg-[#191a1b] dark:border-white/[0.08]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/50 dark:border-white/[0.06] shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {meta.fill ? (
                <img
                  src={meta.logo}
                  alt={meta.name}
                  className="h-9 w-9 rounded-md ring-1 ring-border/60 dark:ring-white/[0.08] object-cover shrink-0"
                />
              ) : (
                <div className="h-9 w-9 rounded-md bg-white ring-1 ring-border/60 dark:ring-white/[0.08] flex items-center justify-center shrink-0 overflow-hidden">
                  <img src={meta.logo} alt={meta.name} className="h-6 w-6 object-contain" />
                </div>
              )}
              <div className="min-w-0">
                <h2
                  id="crm-import-title"
                  className="text-foreground dark:text-[#f7f8f8]"
                  style={{ fontSize: '16px', fontWeight: 590, letterSpacing: '-0.18px', lineHeight: 1.3 }}
                >
                  Import from {meta.name}
                </h2>
                <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                  Pull your {meta.name} contacts into your contact base
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip label="Refresh">
                <button
                  onClick={refresh}
                  disabled={loading}
                  aria-label="Refresh"
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 disabled:opacity-50"
                >
                  <ArrowsClockwise className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </Tooltip>
              <button
                onClick={handleClose}
                aria-label="Close"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto min-h-[280px]">
            {loading ? (
              <div className="h-full flex flex-col items-center justify-center py-20 gap-3">
                <CircleNotch className="h-6 w-6 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" />
                <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '13px' }}>
                  Loading contacts from {meta.name}…
                </p>
              </div>
            ) : loadError ? (
              <div className="h-full flex flex-col items-center justify-center py-16 px-8 text-center gap-3">
                <div className="h-10 w-10 rounded-md bg-red-500/10 dark:bg-red-500/[0.12] flex items-center justify-center">
                  <Warning className="h-5 w-5 text-red-500 dark:text-red-400" />
                </div>
                <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '14px', fontWeight: 590 }}>
                  Couldn't reach {meta.name}
                </p>
                <p className="text-muted-foreground dark:text-[#8a8f98] max-w-md" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                  {loadError}
                </p>
                <Button variant="outline" size="sm" onClick={refresh}>
                  Try again
                </Button>
              </div>
            ) : people.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center py-16 px-8 text-center gap-2">
                <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] flex items-center justify-center mb-1">
                  <Envelope className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
                </div>
                <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '14px', fontWeight: 590 }}>
                  No contacts found
                </p>
                <p className="text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                  {meta.name} returned no records on this page.
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="sticky top-0 bg-card dark:bg-[#191a1b] z-10">
                  <tr className="border-b border-border/50 dark:border-white/[0.06]">
                    <th className="w-10 px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        className="w-3.5 h-3.5 rounded-sm border-border/60 dark:border-white/[0.12] accent-[#5e6ad2]"
                        aria-label="Select all"
                      />
                    </th>
                    {(showTitleLocation ? ['Name', 'Email', 'Company', 'Title', 'Location'] : ['Name', 'Email', 'Company']).map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2.5 text-left text-muted-foreground dark:text-[#8a8f98]"
                        style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {people.map((p, idx) => {
                    const hasEmail = Boolean(p.email);
                    const isChecked = selected.has(idx);
                    const name = p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';
                    const location = [p.city, p.country].filter(Boolean).join(', ');
                    return (
                      <tr
                        key={p.id || idx}
                        onClick={() => hasEmail && toggleSelect(idx)}
                        title={hasEmail ? undefined : `No email address in ${meta.name} — cannot be imported`}
                        className={`border-b border-border/30 dark:border-white/[0.04] transition-colors duration-100 ${
                          hasEmail
                            ? `cursor-pointer ${isChecked ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]' : 'hover:bg-muted/30 dark:hover:bg-white/[0.03]'}`
                            : 'opacity-45'
                        }`}
                      >
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={!hasEmail}
                            onChange={() => toggleSelect(idx)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-3.5 h-3.5 rounded-sm border-border/60 dark:border-white/[0.12] accent-[#5e6ad2] disabled:cursor-not-allowed"
                            aria-label={`Select ${name}`}
                          />
                        </td>
                        <td className="px-3 py-2.5 text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                          {name}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                          {p.email || 'no email'}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                          {p.organization_name || '—'}
                        </td>
                        {showTitleLocation && (
                          <>
                            <td className="px-3 py-2.5 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                              {p.title || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                              {location || '—'}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-border/50 dark:border-white/[0.06] shrink-0 bg-muted/20 dark:bg-white/[0.02] flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Destination list — search an existing list or type a new name to create it */}
              <div className="w-64">
                <ContactListCombobox
                  lists={contactLists.map((l) => ({ id: l.id, name: l.name }))}
                  value={listSel}
                  onChange={setListSel}
                  allowCreate
                  allowClear
                  placeholder="No list — search or create one…"
                  ariaLabel="Destination list for imported records"
                />
              </div>
              {/* Tags */}
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="tags, comma separated"
                title="Tags applied to imported contacts (comma separated)"
                className="h-9 px-3 w-52 rounded-md border bg-background border-border/60 text-foreground placeholder:text-muted-foreground/50 transition-colors duration-150 hover:border-border/80 focus:outline-none focus-visible:border-[#7170ff]/55 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:hover:border-white/[0.14] dark:focus-visible:border-[#7170ff]/75"
                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              />
            </div>

            <div className="flex items-center gap-3">
              {/* Pagination */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={goPrev}
                  disabled={loading || pageIndex === 0}
                  aria-label="Previous page"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretLeft className="h-3.5 w-3.5" />
                </button>
                <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums" style={{ fontSize: '12px' }}>
                  Page {pageIndex + 1}
                </span>
                <button
                  onClick={goNext}
                  disabled={loading || nextCursor == null}
                  aria-label="Next page"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CaretRight className="h-3.5 w-3.5" />
                </button>
              </div>

              <Button
                variant="default"
                size="sm"
                disabled={importing || withEmailCount === 0}
                onClick={handleImport}
                icon={importing
                  ? <CircleNotch className="h-4 w-4 animate-spin" />
                  : undefined}
              >
                {importing
                  ? 'Importing…'
                  : `Import ${withEmailCount > 0 ? withEmailCount : ''} contact${withEmailCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  ), document.body);
}
